/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Wed Oct 04 2023
 **/
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */

import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { v4 } from "uuid";

import { Column, useToast } from "@/frameworks/designStuff/components";
import { Maybe } from "@/frameworks/libraries/components";
import { Configure } from "@/frameworks/libraries/utilities";
import { _T } from "@rfice/language";
import { useChatMessageActions, useRoomState } from "@/frameworks/core/chat";
import { ChatInput } from "@/frameworks/core/components";
import { useEventHandler } from "@rfice/eventhandler";
import { APIErrorCode, EventID } from "@/frameworks/core/commons";
import { RestAPIError } from "@/frameworks/libraries/restservice";
import { usePersistActions, usePersistUser } from "@/frameworks/core/persistence";
import { UnDraggable } from "@/frameworks/libraries/components/Dragger";
import { useChatDataStore } from "@/modules/chat/store";

import type { TypingRefType } from "../Typing";
import Typing from "../Typing";
import ReplyTarget from "./ReplyTarget";
import { BlockInfoPopup } from "./BlockInfoPopup";
import useMessageStore from "./MessageStore";
import { Message } from "./MessageStore";

interface MessageInputProps {
	id: string;
	spaceID?: string;
	type: "Space" | "DM" | "CONFERENCE";
	variant: ChatUI.Size;
	userList: {
		name: string;
		src: string | undefined;
		userID: string;
	}[];
	myRoomID?: string;
	isMyRoom: boolean;
	disabled?: boolean;
	threadID?: string;
	onFilesMessageSent?: () => void;
}

type ChatFileFormat = (
	| ChatServices.Format2.Elements.File
	| ChatServices.Format2.Elements.Image
	| ChatServices.Format2.Elements.Link
) & { fileBoxID: string };

type ReplyTargetType = {
	messageID: string;
	roomID: string;
	message: ChatServices.Format2.Message;
	user: { name: string };
};

function getTextFromChatForm(chatForm: ChatServices.Format2.Message) {
	const recursive = (elements: ChatServices.Format2.Elements.AllRichtextElements[]) => {
		let result = "";
		elements.forEach((element) => {
			switch (element.type) {
				case "text": {
					result += element.text;
					break;
				}
				case "user": {
					result += element.name;
					break;
				}
				case "codeBlock": {
					result += element.code;
					break;
				}
				case "section": {
					result += recursive(element.elements);
					break;
				}
				case "link": {
					result += element.url;
				}
			}
		});
		return result;
	};

	return recursive(chatForm.elements);
}

function MessageInput({ id, variant, onFilesMessageSent, ...rest }: MessageInputProps) {
	const toast = useToast();
	const typingRef = useRef<TypingRefType>(null);
	const { sendMessage } = useChatMessageActions();
	const dispatch = usePersistActions();
	const me = usePersistUser(Configure.userID) as WebServices.User.UserInfo;
	const roomState = useRoomState(id);
	const [replyTarget, setReplyTarget] = useState<ReplyTargetType | undefined>(undefined);
	const { chats } = usePersistActions();
	const userList = roomState?.users ?? [me];
	const isOneOnOne = userList.length === 2;
	const chatUserList = userList
		.map((v) => {
			return dispatch.users.get_user(v.id as string);
		})
		.filter(Boolean);
	const hasBlockedUser = chatUserList?.some((v) => Boolean(v?.blockedByMe));

	const fixMentionStructure = (richText: ChatServices.Format2.Container.Richtext) => {
		//@Description
		//기존 데이터 : {"type":"richtext","elements":[{"type":"section","elements":[{"type":"user","id":"","name":""},{"type":"text","text":" "}]},{"type":"text","text":"안녕"}]}
		//멘션+enter 쳤을 때 복사 붙혀넣기 한 경우 오는 데이터 : {"type":"richtext","elements":[{"type":"text","text":{"mention":{"index":"0","denotationChar":"@","id":"","value":""}}},{"type":"text","text":"안녕"}]}
		//후자와 같이 기존 데이터 형식과 다르게 나올 경우를 위해서 데이터 가공
		return {
			...richText,
			//@ts-ignore
			elements: richText.elements?.map((element: any) => {
				if (element.text?.mention) {
					const mentionInfo = element.text.mention;
					return {
						type: "section",
						elements: [
							{
								type: "user",
								id: mentionInfo.id,
								name: mentionInfo.value
							}
						]
					};
				}
				return element;
			})
		};
	};

	const handleSubmit = async (
		chatForm: ChatServices.Format2.Message,
		files?: ChatFileFormat[],
		isEmergency?: boolean
	) => {
		if (rest.type === "DM" && hasBlockedUser && isOneOnOne) return;
		if (chatForm.elements?.length === 1) {
			if (chatForm.elements[0].type === "text") {
				if (chatForm.elements[0].text === "") {
					chatForm.elements = [];
				}
			}
		}

		const mentionIds = chatForm.elements.reduce<string[]>((total, current: any) => {
			if (Array.isArray(current.elements)) {
				current.elements.forEach((innerElement: { type: string; id: string }) => {
					if (innerElement.type === "user" && innerElement.id) {
						total.push(innerElement.id);
					}
				});
			}
			return total;
		}, []);

		const hasMention = mentionIds.length > 0;
		const mentionType = mentionIds.includes("@all") ? "ALL" : "ID";
		const clientMessageId = v4();

		const localMessage = {
			message: JSON.stringify(fixMentionStructure(chatForm)),
			replyToMessageId: replyTarget?.messageID,
			emergency: Boolean(isEmergency),
			roomId: id,
			mentionIds: hasMention ? mentionIds : undefined,
			mentionType: hasMention ? mentionType : undefined,
			spaceId: rest?.spaceID,
			roomType: "DM",
			sender: {
				id: me.id,
				name: me.name,
				profileImagePath: me.profileImagePath,
				colorProfile: me.colorProfile
			},
			files: files?.map((v) => ({ data: JSON.stringify(v), fileBoxId: v.fileBoxID })),
			timestamp: new Date().getTime(),
			messageId: "",
			clientMessageId
		};

		addMessageToLocal(localMessage as Message);
		updateMessageStatus(clientMessageId, "pending");

		const timeout = setTimeout(() => {
			useMessageStore.getState().updateMessageStatus(clientMessageId, "failed");
		}, 2000);

		sendMessage({
			emergency: Boolean(isEmergency),
			message: JSON.stringify(fixMentionStructure(chatForm)),
			roomId: id,
			mentionIds: hasMention && mentionType === "ID" ? mentionIds : undefined,
			mentionType: hasMention ? mentionType : undefined,
			replyToMessageId: replyTarget?.messageID,
			searchMessage: getTextFromChatForm(chatForm),
			files: files?.map((v) => ({ data: JSON.stringify(v), fileBoxId: v.fileBoxID })),
			threadMessageId: rest.threadID,
			clientMessageId
		})
			.then(() => {
				clearTimeout(timeout);
				if (files?.length) {
					onFilesMessageSent?.();
					chats.save_chat_draft(id, null);
				}
				useMessageStore.getState().removeMessage(clientMessageId);
			})
			.catch((error: RestAPIError) => {
				clearTimeout(timeout);
				updateMessageStatus(clientMessageId, "failed");
				switch (error.code) {
					case APIErrorCode.NOT_ALLOWED_DM_INVITE: {
						toast.show({ type: "error", message: _T("해당 사용자는 DM 수신거부 상태입니다.") });
						return;
					}
					default: {
						toast.show({ type: "error", message: _T("채팅 메시지 전송 실패") });
						return;
					}
				}
			})
			.finally(() => {
				setReplyTarget(undefined);
				typingRef?.current?.done();
			});
	};

	const addMessageToLocal = (message: Message) => {
		useMessageStore.getState().addMessage(message);

		setTimeout(() => {
			const currentMessage = useMessageStore.getState().messages[message?.clientMessageId];
			if (currentMessage?.status === "pending") {
				updateMessageStatus(message.clientMessageId, "failed");
			}
		}, 2000);
	};

	const updateMessageStatus = (clientMessageId: string, state: "pending" | "success" | "failed") => {
		useMessageStore.getState().updateMessageStatus(clientMessageId, state);
	};

	useEventHandler(
		EventID.REPLY_CHAT,
		(replyTarget: ReplyTargetType) => {
			if (replyTarget.roomID !== id) return;
			setReplyTarget(replyTarget);
		},
		[id, rest.spaceID],
		{ disposing: false }
	);

	const cancelReplying = () => {
		setReplyTarget(undefined);
		useChatDataStore.getState().change_replying_state(id, false);
	};

	useEffect(() => {
		if (typingRef?.current?.typing) {
			typingRef.current?.done();
		}
	}, [rest.spaceID, typingRef?.current?.typing]);

	const mentionList = useMemo(() => {
		return userList.map((user) => ({
			id: user.id as string,
			value: user?.name as string,
			profileImagePath: user?.profileImagePath as string,
			name: user.name as string
		}));
	}, [me, userList]);

	const isDisabled = useCallback(() => {
		// 나와의 대화
		if (userList.length === 1 && userList[0].id === Configure.userID) {
			return false;
		}

		// 차단된 사용자와 1대1 DM
		if (rest.type === "DM" && hasBlockedUser && isOneOnOne) {
			return true;
		}
	}, [me]);

	return (
		<UnDraggable>
			<InputForm variant={variant} onSubmit={(ev: any) => ev.preventDefault()}>
				<ReplyTarget replyTarget={replyTarget} roomID={id} onCancel={cancelReplying} />
				<Maybe test={hasBlockedUser}>
					<BlockInfoPopup type={rest.type} userIDs={userList.map((v) => v.id as string)} variant={variant} />
				</Maybe>
				{/*<Maybe test={userList.length > 0}>*/}
				<ChatInput
					disableAllTagMention={Boolean(rest.threadID)}
					channelID={id}
					onSubmit={handleSubmit}
					css={ChatInputCSS}
					disabled={rest.disabled ?? isDisabled()}
					onTyping={typingRef?.current?.typing}
					atMentions={mentionList}
					variant={variant}
					myRoomID={rest.isMyRoom ? rest.spaceID : rest.myRoomID}
					spaceID={rest.spaceID}
					threadID={rest.threadID}
					type={rest.type}
					isMyRoom={rest.isMyRoom}
					isHideSiren={
						isOneOnOne || Boolean(rest.threadID) || useChatDataStore.getState().space_is_audio_zone
					}
					isHideVote={
						rest.isMyRoom ||
						(userList.length === 1 && roomState?.roomType === "DM") ||
						Boolean(rest.threadID)
					}
				>
					<Typing ref={typingRef} roomID={id} variant={variant} />
				</ChatInput>
				{/*</Maybe>*/}
			</InputForm>
		</UnDraggable>
	);
}

const InputForm = styled(Column.form)<{ variant: ChatUI.Size }>`
	position: relative;
	z-index: 1;
	width: ${({ variant }) => {
		if (variant === "transparent") {
			return "calc(100% - 8px)";
		}
		return "-webkit-fill-available";
	}};
	border-radius: 12px;
	${({ variant }) =>
		variant === "fullSize" &&
		css`
			padding: 16px 60px;
		`}
	aside.EmojiPickerReact.epr-main {
		--epr-emoji-size: 20px;
		--epr-category-navigation-button-size: 20px;
		--epr-header-padding: 12px 12px 6px 12px;
		--epr-search-input-height: 30px;
		--epr-category-label-height: 30px;
		--epr-preview-height: 40px;
	}
	> div > ul {
		border-radius: 12px;
	}
`;

const ChatInputCSS = css`
	.ql-toolbar {
		//border-radius: 0 !important;
	}
`;

export default memo(MessageInput);
